import { authorizedFetch } from '../../helpers/authorizedFetch'
import { formatDate } from '../../helpers/date'
import trans from '../../helpers/trans'
import { generateApiUrl, generateApiV3Url } from '../../helpers/url'
import getIncluded from '../../japi/getIncluded'
import isResourceDocument from '../../japi/guards/isResourceDocument'
import { JapiDocument } from '../../japi/types/Document'
import { DateFormat } from '../../types/dateFormatTypes'
import { FetchCollectionResult, FetchResult, FetchResultType } from '../FetchResult'
import { NotificationCenterHeaderName } from '../NotificationCenter/NotificationCenter'
import { UserResource } from '../User/User'
import {
    AddCommentFormData,
    Comment,
    CommentResource,
    ConceptDayComment,
    ConceptDayCommentV3Resource,
} from './Comment'
import {
    transformCommentFormDataToRequest,
    transformToComment,
    transformToConceptDayComment,
} from './CommentTransformers'

let postCommentApiCallAbortController = new AbortController()

export const postCommentApiCall = async (
    addCommentFormData: AddCommentFormData,
    useAbortController = false,
): Promise<FetchResult<Comment, string>> => {
    if (useAbortController) {
        postCommentApiCallAbortController.abort()
        postCommentApiCallAbortController = new AbortController()
    }

    const commentRequest = transformCommentFormDataToRequest(addCommentFormData)

    try {
        const url = generateApiUrl({
            endpoint: '/comments',
            queryParams: {
                include: 'owner',
            },
        })

        const response = await authorizedFetch(url, {
            method: 'POST',
            body: JSON.stringify({ data: commentRequest }),
            ...(useAbortController && { signal: postCommentApiCallAbortController.signal }),
            ...addCommentFormData.eventLogTrackerData && {
                headers: {
                    [NotificationCenterHeaderName.X_EVENT]: JSON.stringify(addCommentFormData.eventLogTrackerData),
                },
            },
        })

        const doc: JapiDocument = await response.json()

        if (! isResourceDocument<CommentResource>(doc) || ! doc.data) {
            return {
                status: response.status,
                type: FetchResultType.Error,
                error: trans('errors.unknownError'),
            }
        }

        const userResource = getIncluded(doc, doc.data, 'owner') as UserResource
        const comment = transformToComment(doc.data, userResource)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: comment,
        }
    } catch (error) {
        console.error('[postCommentApiCall]', error)

        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}

export const getConceptDayCommentsApiCall = async (
    startDate?: Date,
    endDate?: Date,
    departments?: string[],
): Promise<FetchCollectionResult<ConceptDayComment[], string>> => {
    try {
        const url = generateApiV3Url({
            endpoint: '/shift/concepts/day-comments',
            queryParams: {
                ...(startDate && { startDate: formatDate(startDate, DateFormat.apiDate) }),
                ...(endDate && { endDate: formatDate(endDate, DateFormat.apiDate) }),
                ...(departments && { 'departments[]': departments }),
            },
        })

        const response = await authorizedFetch(url)

        const commentResources: ConceptDayCommentV3Resource[] = await response.json()

        const comments = commentResources.map(transformToConceptDayComment)

        return {
            status: response.status,
            type: FetchResultType.Success,
            data: comments,
            amountOfPages: 1,
        }
    } catch (error) {
        console.error('[getConceptDayCommentsApiCall]', error)
        return {
            status: 500,
            type: FetchResultType.Error,
            error: trans('errors.unknownError'),
        }
    }
}
